/**
 * Authenticated HTTP client. Mirrors iOS ChatClient — pulls the JWT from
 * storage on each call, refreshes lazily on 401, surfaces all other
 * errors as ApiError.
 *
 * Refresh-on-401 contract:
 *   1. First call: send Authorization: Bearer <accessToken>
 *   2. If 401: call /auth/refresh with refreshToken
 *   3. On refresh success: retry the original request once
 *   4. On refresh failure: force sign-out (clears tokens, kicks to /sign-in)
 *
 * Callers register a forceSignOut handler at app boot (typically from
 * AuthProvider) so the API client can trigger UI-level sign-out without
 * a circular import.
 */

import { backendUrl } from "@/shared/config";
import { refreshSession } from "@/features/auth/lib/authClient";
import type {
  BankrollPoint,
  Breakdown,
  BreakdownDimension,
  BuiltHand,
  ChatStreamEvent,
  ConversationDetail,
  ConversationSummary,
  HandSummary,
  LeakReport,
  LiveSession,
  LogSessionInput,
  Message,
  ProfileSummary,
  RecommendedDrill,
  SavedHand,
  SessionFilter,
  SessionStats,
  SheetMeta,
  SkillHistoryResponse,
  SkillsResponse,
  SpotAction,
  TrackerHome,
  UpdateSessionInput,
} from "@/shared/models";
import {
  clearTokens,
  isAccessTokenExpired,
  loadTokens,
  saveTokens,
} from "@/features/auth/lib/tokenStorage";

/**
 * Thrown by any helper in this module on a non-2xx response (or auth
 * failure). Carries the HTTP status + raw body text alongside the
 * extracted `detail` message so error UIs can show whichever is most
 * useful.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly bodyText?: string;
  constructor(status: number, message: string, bodyText?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.bodyText = bodyText;
  }
}

/**
 * Specialization of ApiError for the 402 cap-reached gate on /chat.
 * The backend's HTTPException carries a structured detail object
 * with the active gating window so the UpgradeModal can render
 * appropriate copy:
 *
 *   `{code: "cap_reached", window, gate_type,
 *     subscription_status, plan_level, used, limit}`
 *
 * - `gate_type === "hard_wall"` → a free user past their first leak
 *   report: no further coaching, no reset → subscribe-to-continue copy.
 * - `gate_type === "soft_cap"` + `window === "monthly"` → a paid monthly
 *   cap (core 300, plus 1000) → reset-countdown copy.
 *
 * Throws into the ChatView async generator; ChatView catches the type
 * via instanceof, shows the modal, and aborts the stream cleanly
 * without surfacing as a generic error.
 */
export class CapReachedError extends ApiError {
  readonly window: "daily" | "monthly" | "none";
  readonly gateType: "soft_cap" | "hard_wall" | "none";
  readonly subscriptionStatus: string;
  readonly planLevel: string | null;
  readonly used: number;
  readonly limit: number;
  constructor(detail: {
    window: "daily" | "monthly" | "none";
    gate_type: "soft_cap" | "hard_wall" | "none";
    subscription_status: string;
    plan_level: string | null;
    used: number;
    limit: number;
  }) {
    super(
      402,
      detail.gate_type === "hard_wall"
        ? "First leak report reached — subscribe to keep drilling"
        : `Monthly limit reached (${detail.used} / ${detail.limit})`,
    );
    this.name = "CapReachedError";
    this.window = detail.window;
    this.gateType = detail.gate_type;
    this.subscriptionStatus = detail.subscription_status;
    this.planLevel = detail.plan_level;
    this.used = detail.used;
    this.limit = detail.limit;
  }
}

// Callers register a force-sign-out handler so we can kick to /sign-in
// when refresh fails. Set once at app boot from AuthProvider.
let onForceSignOut: (() => void) | null = null;

/**
 * Register a callback the API client will invoke when the refresh token
 * is dead (so the UI can route to /sign-in). Wired once at app boot
 * from AuthProvider; setting it from elsewhere would create a circular
 * import.
 */
export function registerForceSignOut(handler: () => void): void {
  onForceSignOut = handler;
}

// ----------------------------------------------------------------------------
// Internal — authed fetch with refresh-on-401
// ----------------------------------------------------------------------------

interface AuthedFetchOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  /** JSON-serialized into the body. Mutually exclusive with `formData`. */
  body?: unknown;
  /** Sent as multipart/form-data — used for the feedback endpoint's
   *  optional file upload. Browser sets Content-Type w/ boundary itself,
   *  so we must NOT set Content-Type when this is present. */
  formData?: FormData;
  headers?: Record<string, string>;
  /** When true, returns the raw Response (for SSE streaming). */
  returnRaw?: boolean;
}

async function authedFetch(
  path: string,
  options: AuthedFetchOptions = {},
): Promise<Response> {
  const tokens = loadTokens();
  if (!tokens) {
    onForceSignOut?.();
    throw new ApiError(401, "Not signed in");
  }

  // Proactive refresh if access token is about to expire — saves a round trip.
  let accessToken = tokens.accessToken;
  if (isAccessTokenExpired(tokens)) {
    try {
      const fresh = await refreshSession(tokens.refreshToken);
      saveTokens(fresh);
      accessToken = fresh.accessToken;
    } catch {
      clearTokens();
      onForceSignOut?.();
      throw new ApiError(401, "Session expired");
    }
  }

  const doRequest = async (token: string): Promise<Response> => {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      ...(options.headers ?? {}),
    };
    let body: BodyInit | undefined;
    if (options.formData !== undefined) {
      // Let the browser set Content-Type (with boundary) automatically.
      body = options.formData;
    } else if (options.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    }
    return fetch(backendUrl(path), {
      method: options.method ?? "GET",
      headers,
      body,
    });
  };

  let response = await doRequest(accessToken);

  // Lazy refresh on 401 — covers the race where the proactive check
  // passed but the server-side clock disagreed.
  if (response.status === 401) {
    try {
      const fresh = await refreshSession(tokens.refreshToken);
      saveTokens(fresh);
      response = await doRequest(fresh.accessToken);
    } catch {
      clearTokens();
      onForceSignOut?.();
      throw new ApiError(401, "Session expired");
    }
  }

  if (!response.ok && !options.returnRaw) {
    const text = await response.text().catch(() => "");
    let detail: string | undefined;
    try {
      detail = JSON.parse(text)?.detail;
    } catch {
      // not JSON
    }
    throw new ApiError(
      response.status,
      detail ?? `Request failed: ${response.status} ${response.statusText}`,
      text,
    );
  }

  return response;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await authedFetch(path);
  return res.json() as Promise<T>;
}

// ----------------------------------------------------------------------------
// Backend → TS field mappers (snake_case → camelCase)
// ----------------------------------------------------------------------------

interface RawMessage {
  id?: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}
/** Convert one backend message row into the camelCase TS shape. */
export function mapMessage(raw: RawMessage): Message {
  return {
    id: raw.id ?? cryptoRandomId(),
    role: raw.role,
    content: raw.content,
    timestamp: raw.timestamp,
  };
}

interface RawConversationListItem {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}
/** Convert one conversation-list row from the backend wire shape. */
export function mapConversationSummary(raw: RawConversationListItem): ConversationSummary {
  return {
    id: raw.id,
    title: raw.title,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    messageCount: raw.message_count,
  };
}

interface RawConversationDetail {
  id: string;
  title?: string;
  user_title?: string;
  inferred_title?: string;
  mode?: string;
  mode_addendum?: string;
  messages: RawMessage[];
  created_at: string;
  updated_at: string;
}
/**
 * Convert a full conversation doc into the TS shape, applying the
 * user_title > inferred_title > fallback display-title precedence
 * that matches backend `effective_title()`.
 */
export function mapConversationDetail(raw: RawConversationDetail): ConversationDetail {
  // Title precedence matches backend `effective_title()`:
  // user_title (user-renamed) > inferred_title (LLM-set) > fallback.
  // `raw.title` is a defensive fallback for endpoints that return a
  // pre-computed title field (list endpoint does this) — it should
  // basically never fire on the detail endpoint, which returns raw
  // Mongo doc with only user_title + inferred_title.
  const title =
    raw.user_title?.trim() ||
    raw.inferred_title?.trim() ||
    raw.title?.trim() ||
    "Untitled";
  return {
    id: raw.id,
    title,
    mode: raw.mode,
    modeAddendum: raw.mode_addendum,
    messages: raw.messages.map(mapMessage),
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

interface RawSkillState {
  skill_id: string;
  name: string;
  category: string;
  sheet_id: string;
  description: string;
  level: number;
  confidence: number;
  trend: "up" | "steady" | "down";
  signal_count: number;
  last_assessed: string | null;
  recent_evidence: string[];
}
interface RawSkillsResponse {
  skills: RawSkillState[];
  total_signals: number;
}
/** Map the /skills response into the camelCase TS shape. */
export function mapSkills(raw: RawSkillsResponse): SkillsResponse {
  return {
    totalSignals: raw.total_signals,
    skills: raw.skills.map((s) => ({
      skillId: s.skill_id,
      name: s.name,
      category: s.category,
      sheetId: s.sheet_id,
      description: s.description,
      level: s.level,
      confidence: s.confidence,
      trend: s.trend,
      signalCount: s.signal_count,
      lastAssessed: s.last_assessed,
      recentEvidence: s.recent_evidence ?? [],
    })),
  };
}

interface RawSkillHistoryPoint {
  date: string;
  level: number;
  confidence: number;
  signal_count: number;
}
interface RawSkillHistoryResponse {
  skill_id: string | null;
  points: RawSkillHistoryPoint[];
}
/** Map the /skills/history response into the camelCase TS shape. */
export function mapSkillHistory(raw: RawSkillHistoryResponse): SkillHistoryResponse {
  return {
    skillId: raw.skill_id,
    points: (raw.points ?? []).map((p) => ({
      date: p.date,
      level: p.level,
      confidence: p.confidence,
      signalCount: p.signal_count,
    })),
  };
}

interface RawProfileSummary {
  investment: {
    active_since: string | null;
    days_active: number;
    drills_completed: number;
    signals_logged: number;
    current_streak: number;
    longest_streak: number;
  };
  identity: {
    ready: boolean;
    top_skills: { skill_id: string; name: string; level?: number | null }[];
    top_category: string | null;
    focus_skill: { skill_id: string; name: string; level?: number | null } | null;
  };
  milestones: {
    date: string;
    kind: "started" | "drills" | "leak_report" | "graduation" | "overall";
    title: string;
    detail: string | null;
    skill_id: string | null;
  }[];
  leaks: {
    open: { skill_id: string | null; name: string | null; title: string; severity: string | null }[];
    closed: {
      skill_id: string;
      name: string | null;
      title: string;
      flagged_at: string;
      cleared_by: string;
    }[];
  };
}

const mapSkillRef = (r: { skill_id: string; name: string; level?: number | null }) => ({
  skillId: r.skill_id,
  name: r.name,
  level: r.level ?? null,
});

/** Map the /profile-summary response into the camelCase TS shape. */
export function mapProfileSummary(raw: RawProfileSummary): ProfileSummary {
  return {
    investment: {
      activeSince: raw.investment.active_since,
      daysActive: raw.investment.days_active,
      drillsCompleted: raw.investment.drills_completed,
      signalsLogged: raw.investment.signals_logged,
      currentStreak: raw.investment.current_streak,
      longestStreak: raw.investment.longest_streak,
    },
    identity: {
      ready: raw.identity.ready,
      topSkills: (raw.identity.top_skills ?? []).map(mapSkillRef),
      topCategory: raw.identity.top_category,
      focusSkill: raw.identity.focus_skill ? mapSkillRef(raw.identity.focus_skill) : null,
    },
    milestones: (raw.milestones ?? []).map((m) => ({
      date: m.date,
      kind: m.kind,
      title: m.title,
      detail: m.detail,
      skillId: m.skill_id,
    })),
    leaks: {
      open: (raw.leaks?.open ?? []).map((o) => ({
        skillId: o.skill_id,
        name: o.name,
        title: o.title,
        severity: o.severity,
      })),
      closed: (raw.leaks?.closed ?? []).map((c) => ({
        skillId: c.skill_id,
        name: c.name,
        title: c.title,
        flaggedAt: c.flagged_at,
        clearedBy: c.cleared_by,
      })),
    },
  };
}

/** Body for POST /library-spots/report. Web sends the spot JSON +
 *  the conversation it was seen in + optional notes; backend derives
 *  content_hash from the spot to locate the library doc. */
export interface ReportSpotRequest {
  spot: unknown; // SpotPayload: typed as unknown here since we serialize it raw
  conversationId: string;
  notes: string;
}

export interface ReportSpotResponse {
  reportId: string;
  movedFromLibrary: boolean;
  originalSpotId: string | null;
}

export async function reportLibrarySpot(
  req: ReportSpotRequest,
): Promise<ReportSpotResponse> {
  const res = await authedFetch("/library-spots/report", {
    method: "POST",
    body: {
      spot: req.spot,
      conversation_id: req.conversationId,
      notes: req.notes,
    },
  });
  // authedFetch returns a Response — we still need to parse the JSON body.
  const data = (await res.json()) as {
    report_id: string;
    moved_from_library: boolean;
    original_spot_id: string | null;
  };
  return {
    reportId: data.report_id,
    movedFromLibrary: data.moved_from_library,
    originalSpotId: data.original_spot_id,
  };
}

// ---------------------------------------------------------------------------
// Hand Analyzer — save a built hand (free) + list/get/delete. The coach grade
// itself rides the normal streamChat path (mode="hand_analysis", handId).
// ---------------------------------------------------------------------------

/** Wire shape (snake_case) for a BuiltHand — only the hero/effective-stack/
 *  ended keys differ from camelCase; street + action + villain keys are shared. */
interface WireBuiltHand {
  hero_position: string;
  hero_cards: [string, string];
  effective_stack_bb: number;
  villains: { position: string; cards: [string, string] | null }[];
  streets: BuiltHand["streets"];
  ended_on: BuiltHand["endedOn"];
  notes?: string | null;
}

function builtHandToWire(h: BuiltHand): WireBuiltHand {
  return {
    hero_position: h.heroPosition,
    hero_cards: h.heroCards,
    effective_stack_bb: h.effectiveStackBb,
    villains: h.villains,
    streets: h.streets,
    ended_on: h.endedOn,
    notes: h.notes ?? null,
  };
}

function wireToBuiltHand(w: WireBuiltHand): BuiltHand {
  return {
    heroPosition: w.hero_position,
    heroCards: w.hero_cards,
    effectiveStackBb: w.effective_stack_bb,
    villains: w.villains,
    streets: w.streets,
    endedOn: w.ended_on,
    notes: w.notes ?? undefined,
  };
}

interface RawSavedHand {
  id: string;
  title: string | null;
  hand: WireBuiltHand;
  analysis_conversation_id: string | null;
  session_id: string | null;
  created_at: string;
}

function mapSavedHand(raw: RawSavedHand): SavedHand {
  return {
    id: raw.id,
    title: raw.title,
    hand: wireToBuiltHand(raw.hand),
    analysisConversationId: raw.analysis_conversation_id,
    sessionId: raw.session_id,
    createdAt: raw.created_at,
  };
}

/** All saved hands, or — with `sessionId` — only the hands attached to that
 *  logged session (the tracker's per-session "hands you remember" list). */
export async function listHands(sessionId?: string): Promise<HandSummary[]> {
  const path = sessionId
    ? `/hands?session_id=${encodeURIComponent(sessionId)}`
    : "/hands";
  const raw = await getJson<
    {
      id: string;
      title: string | null;
      summary: string;
      analyzed: boolean;
      session_id: string | null;
      created_at: string;
    }[]
  >(path);
  return raw.map((r) => ({
    id: r.id,
    title: r.title,
    summary: r.summary,
    analyzed: r.analyzed,
    sessionId: r.session_id,
    createdAt: r.created_at,
  }));
}

export async function saveHand(
  hand: BuiltHand,
  title?: string,
  sessionId?: string,
): Promise<SavedHand> {
  const res = await authedFetch("/hands", {
    method: "POST",
    body: {
      hand: builtHandToWire(hand),
      title: title ?? null,
      session_id: sessionId ?? null,
    },
  });
  return mapSavedHand((await res.json()) as RawSavedHand);
}

export async function getHand(id: string): Promise<SavedHand> {
  const raw = await getJson<RawSavedHand>(`/hands/${id}`);
  return mapSavedHand(raw);
}

export async function deleteHand(id: string): Promise<void> {
  await authedFetch(`/hands/${id}`, { method: "DELETE" });
}

/** Body for POST /quizzes/attempts. `question` is intentionally
 *  opaque — different quiz types carry different payloads (e.g.
 *  open/fold sends `{position, hand}`). */
export interface QuizAttemptRequest {
  quizType: string;
  question: Record<string, unknown>;
  userAnswer: string;
  correctAnswer: string;
}

export interface QuizAttemptResponse {
  attemptId: string;
  isCorrect: boolean;
}

/** Body for POST /feedback. Multipart-form because of the optional file. */
export interface FeedbackSubmitRequest {
  text: string;
  screenshot?: File | null;
  /** Auto-captured by the caller: `window.location.href` at submit time. */
  pageUrl?: string;
  /** Auto-captured if the user was on /chat/{id} when they opened feedback. */
  conversationId?: string;
}

export interface FeedbackSubmitResponse {
  feedbackId: string;
  hasScreenshot: boolean;
}

export async function submitFeedback(
  req: FeedbackSubmitRequest,
): Promise<FeedbackSubmitResponse> {
  const fd = new FormData();
  fd.append("text", req.text);
  if (req.pageUrl) fd.append("page_url", req.pageUrl);
  if (req.conversationId) fd.append("conversation_id", req.conversationId);
  if (req.screenshot) fd.append("screenshot", req.screenshot);
  const res = await authedFetch("/feedback", {
    method: "POST",
    formData: fd,
  });
  const data = (await res.json()) as { feedback_id: string; has_screenshot: boolean };
  return {
    feedbackId: data.feedback_id,
    hasScreenshot: data.has_screenshot,
  };
}

export async function recordQuizAttempt(
  req: QuizAttemptRequest,
): Promise<QuizAttemptResponse> {
  const res = await authedFetch("/quizzes/attempts", {
    method: "POST",
    body: {
      quiz_type: req.quizType,
      question: req.question,
      user_answer: req.userAnswer,
      correct_answer: req.correctAnswer,
    },
  });
  const data = (await res.json()) as { attempt_id: string; is_correct: boolean };
  return {
    attemptId: data.attempt_id,
    isCorrect: data.is_correct,
  };
}

interface RawRecommendedDrill {
  skill_id: string;
  skill_name: string;
  strategy: string;
  kickoff_message: string;
  rationale: string;
  drill_target: string;
  drill_difficulty: "foundational" | "default" | "edge_case";
}
/** Map the /recommended-drill response into the camelCase TS shape. */
export function mapRecommendedDrill(raw: RawRecommendedDrill): RecommendedDrill {
  return {
    skillId: raw.skill_id,
    skillName: raw.skill_name,
    strategy: raw.strategy,
    kickoffMessage: raw.kickoff_message,
    rationale: raw.rationale,
    drillTarget: raw.drill_target,
    drillDifficulty: raw.drill_difficulty,
  };
}

interface RawLeakPattern {
  id: string;
  title: string;
  description: string;
  severity: "high" | "medium" | "low";
  evidence: string[];
  recommended_action: string;
  recommended_skill: string;
}
interface RawLeakReport {
  patterns: RawLeakPattern[];
  recommended_focus: string;
  signal_count: number;
  generated_at: string;
  insufficient_signals: boolean;
  signal_count_at_last_report: number;
  signals_until_next_report: number;
}
/** Map the /leak-report response into the camelCase TS shape. */
export function mapLeakReport(raw: RawLeakReport): LeakReport {
  return {
    patterns: raw.patterns.map((p) => ({
      id: p.id,
      title: p.title,
      description: p.description,
      severity: p.severity,
      evidence: p.evidence,
      recommendedAction: p.recommended_action,
      recommendedSkill: p.recommended_skill,
    })),
    recommendedFocus: raw.recommended_focus,
    signalCount: raw.signal_count,
    generatedAt: raw.generated_at,
    insufficientSignals: raw.insufficient_signals,
    signalCountAtLastReport: raw.signal_count_at_last_report,
    signalsUntilNextReport: raw.signals_until_next_report,
  };
}

function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------

export async function listConversations(): Promise<ConversationSummary[]> {
  const raw = await getJson<RawConversationListItem[]>("/conversations");
  return raw.map(mapConversationSummary);
}

export async function getConversation(id: string): Promise<ConversationDetail> {
  const raw = await getJson<RawConversationDetail>(`/conversations/${id}`);
  return mapConversationDetail(raw);
}

export async function renameConversation(
  id: string,
  title: string,
): Promise<void> {
  await authedFetch(`/conversations/${id}`, {
    method: "PATCH",
    body: { title },
  });
}

export async function archiveConversation(id: string): Promise<void> {
  await authedFetch(`/conversations/${id}/archive`, { method: "POST" });
}

export async function fetchSkills(): Promise<SkillsResponse> {
  const raw = await getJson<RawSkillsResponse>("/skills");
  return mapSkills(raw);
}

export async function fetchProfileSummary(): Promise<ProfileSummary> {
  const raw = await getJson<RawProfileSummary>("/profile-summary");
  return mapProfileSummary(raw);
}

export async function fetchOverallSkillHistory(): Promise<SkillHistoryResponse> {
  const raw = await getJson<RawSkillHistoryResponse>("/skills/history");
  return mapSkillHistory(raw);
}

export async function fetchSkillHistory(skillId: string): Promise<SkillHistoryResponse> {
  const raw = await getJson<RawSkillHistoryResponse>(
    `/skills/${encodeURIComponent(skillId)}/history`,
  );
  return mapSkillHistory(raw);
}

export async function resetSkills(): Promise<void> {
  await authedFetch("/skills", { method: "DELETE" });
}

export async function fetchRecommendedDrill(): Promise<RecommendedDrill> {
  const raw = await getJson<RawRecommendedDrill>("/recommended-drill");
  return mapRecommendedDrill(raw);
}

/**
 * Fetch the current leak report. The backend always returns the most
 * recently stored report; a new one is generated server-side only when
 * 10 new signals have accumulated since the last regeneration. There
 * is no on-demand refresh path — the response itself carries
 * `signalsUntilNextReport` to drive the progress UI.
 */
export async function fetchLeakReport(): Promise<LeakReport> {
  const raw = await getJson<RawLeakReport>("/leak-report");
  return mapLeakReport(raw);
}

// ----------------------------------------------------------------------------
// Study sheets
// ----------------------------------------------------------------------------

interface RawSheetMeta {
  id: string;
  display_name: string;
  number: number;
  category: string;
}

export async function listSheets(): Promise<SheetMeta[]> {
  const raw = await getJson<RawSheetMeta[]>("/sheets");
  return raw.map((s) => ({
    id: s.id,
    displayName: s.display_name,
    number: s.number,
    category: s.category,
  }));
}

export async function fetchSheetContent(id: string): Promise<string> {
  const raw = await getJson<{ id: string; content: string }>(`/sheets/${id}`);
  return raw.content;
}

// ----------------------------------------------------------------------------
// Chat streaming (SSE)
// ----------------------------------------------------------------------------

export interface ChatStreamRequest {
  message: string;
  conversationId?: string;
  mode?: string;
  /** Structured drill metadata — what skill the drill is exercising and
   * how hard the spots should be. Optional; backend treats absence as
   * "not a drill" (no library lookup attempted). The backend composes
   * the LLM-facing addendum prose server-side from these fields, so
   * clients never carry prose around. */
  drillTarget?: string;
  drillDifficulty?: "foundational" | "default" | "edge_case";
  /** Today's Drill: the user's local date (YYYY-MM-DD). When set, the
   * backend serves the shared pinned daily spot, treats the turn as
   * cap-exempt, and advances the streak on ladder completion. */
  dailyChallengeDate?: string;
  /** Session-tracker coaching: binds the chat to a logged session so the
   * backend injects that session's [SESSION FACTS] and routes the coach's
   * skill_signals to the session_insights stream. Set by `session_debrief`. */
  sessionId?: string;
  /** Scope the session-coaching [SESSION FACTS] to one stake/location. */
  sessionStake?: string;
  sessionLocation?: string;
  /** Hand Analyzer: binds the chat to a saved built hand so the backend
   * injects its deterministic [HAND REPLAY] block and the coach grades the
   * line. Set by `hand_analysis` mode. */
  handId?: string;
}

/**
 * Open a streaming chat request to /chat. Yields SSE events one at a
 * time. Caller is responsible for awaiting completion (the generator
 * returns after the final event).
 */
export async function* streamChat(
  req: ChatStreamRequest,
): AsyncGenerator<ChatStreamEvent> {
  const response = await authedFetch("/chat", {
    method: "POST",
    body: {
      message: req.message,
      conversation_id: req.conversationId,
      mode: req.mode,
      drill_target: req.drillTarget,
      drill_difficulty: req.drillDifficulty,
      daily_challenge_date: req.dailyChallengeDate,
      session_id: req.sessionId,
      session_stake: req.sessionStake,
      session_location: req.sessionLocation,
      hand_id: req.handId,
    },
    headers: { Accept: "text/event-stream" },
    returnRaw: true,
  });

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => "");
    // 402 with a structured `detail.code = "cap_reached"` is the gate
    // firing — surface as the typed CapReachedError so ChatView can
    // pop the upgrade modal with window-appropriate copy.
    if (response.status === 402) {
      try {
        const parsed = JSON.parse(text) as { detail?: unknown };
        const d = parsed.detail;
        if (
          d &&
          typeof d === "object" &&
          (d as Record<string, unknown>).code === "cap_reached"
        ) {
          const det = d as {
            window?: unknown;
            gate_type?: unknown;
            subscription_status?: unknown;
            plan_level?: unknown;
            used?: unknown;
            limit?: unknown;
          };
          const gateType =
            det.gate_type === "hard_wall"
              ? "hard_wall"
              : det.gate_type === "soft_cap"
                ? "soft_cap"
                : "none";
          const window =
            det.window === "monthly"
              ? "monthly"
              : det.window === "daily"
                ? "daily"
                : "none";
          throw new CapReachedError({
            window,
            gate_type: gateType,
            subscription_status:
              typeof det.subscription_status === "string"
                ? det.subscription_status
                : "free",
            plan_level:
              typeof det.plan_level === "string" ? det.plan_level : null,
            used: typeof det.used === "number" ? det.used : 0,
            limit: typeof det.limit === "number" ? det.limit : 5,
          });
        }
      } catch (err) {
        if (err instanceof CapReachedError) throw err;
        // Body wasn't the expected shape — fall through to generic 402.
      }
    }
    throw new ApiError(response.status, "Chat stream failed to open", text);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line ("\n\n"). Within a frame,
      // event: <name> and data: <payload> are on separate lines.
      let nl: number;
      while ((nl = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 2);
        const event = parseSseFrame(frame);
        if (event) yield event;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Decode one SSE frame (event + data lines, blank-line terminated)
 * into a typed ChatStreamEvent. Backend sends JSON-wrapped payloads
 * for meta/token/error so we double-parse; falls back to raw text
 * if the data line isn't valid JSON.
 */
function parseSseFrame(frame: string): ChatStreamEvent | null {
  let eventName: string | null = null;
  let data = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) eventName = line.slice(6).trim();
    else if (line.startsWith("data:")) data += line.slice(5).trim();
  }
  if (!eventName) return null;

  switch (eventName) {
    case "meta": {
      try {
        const parsed = JSON.parse(data) as { conversation_id?: string };
        if (parsed.conversation_id) {
          return { kind: "meta", conversationId: parsed.conversation_id };
        }
      } catch {
        // fall through
      }
      return null;
    }
    case "token": {
      // Backend sends `{"text": "..."}` as a JSON object — extract .text.
      // (Same shape iOS consumes; see backend main.py `_sse("token", {"text": text})`.)
      try {
        const parsed = JSON.parse(data) as { text?: string };
        if (typeof parsed.text === "string") {
          return { kind: "token", chunk: parsed.text };
        }
      } catch {
        // not JSON — fall through to raw data
      }
      return { kind: "token", chunk: data };
    }
    case "done":
      return { kind: "done" };
    case "error": {
      // Backend sends `{"message": "..."}` for errors.
      try {
        const parsed = JSON.parse(data) as { message?: string };
        return { kind: "error", message: parsed.message ?? "Stream error" };
      } catch {
        return { kind: "error", message: data || "Stream error" };
      }
    }
    default:
      return null;
  }
}

// Format helpers for sending action choices as user messages (mirrors iOS).
//
// When the spot context is provided, the formatted message includes the
// pot-fraction (for bets) or multiple-of-prior-bet (for raises) so the
// assistant interprets the sizing the same way the UI displays it. The
// preset buttons show "33%" or "3×" while the bb-amount sent could be
// any number — without the context suffix, a "33% pot" preset on a
// 100bb pot reads to the assistant as a 33bb bet (which is wildly
// different from a 33% pot bet on a small pot).
export function formatActionChoice(
  action: SpotAction,
  spot?: import("@/shared/models").SpotPayload,
): string {
  switch (action.action) {
    case "fold":
      return "I'd fold.";
    case "check":
      return "I'd check.";
    case "call":
      return "I'd call.";
    case "bet":
      if (action.amount == null) return "I'd bet.";
      return `I'd bet ${formatBb(action.amount)}${potSuffix(action.amount, spot)}.`;
    case "raise":
      if (action.amount == null) return "I'd raise.";
      return `I'd raise to ${formatBb(action.amount)}${raiseSuffix(action.amount, spot)}.`;
  }
}

/** "(~33% pot)" suffix for bets, when we have spot context. */
function potSuffix(
  amount: number,
  spot?: import("@/shared/models").SpotPayload,
): string {
  if (!spot || spot.pot <= 0) return "";
  const pct = Math.round((amount / spot.pot) * 100);
  return ` (~${pct}% pot)`;
}

/** "(2.5× the prior bet)" suffix for raises, when we have spot context. */
function raiseSuffix(
  amount: number,
  spot?: import("@/shared/models").SpotPayload,
): string {
  if (!spot) return "";
  // Most recent non-Hero bet/raise amount — the "thing we're raising over."
  let prev = 0;
  for (let i = spot.history.length - 1; i >= 0; i--) {
    const h = spot.history[i]!;
    if (h.actor === "Hero") continue;
    if ((h.action === "bet" || h.action === "raise") && h.amount && h.amount > 0) {
      prev = h.amount;
      break;
    }
  }
  if (prev <= 0) return "";
  const mult = Math.round((amount / prev) * 10) / 10;
  return ` (${mult}× the prior bet)`;
}

function formatBb(amount: number): string {
  const rounded = Math.round(amount * 10) / 10;
  return `${rounded}bb`;
}

// ----------------------------------------------------------------------------
// Session & bankroll tracker
// ----------------------------------------------------------------------------

interface RawNotableHand {
  summary: string;
  skill?: string | null;
  outcome?: "won" | "lost" | "chopped" | null;
  street?: "preflop" | "flop" | "turn" | "river" | null;
}

interface RawSession {
  id: string;
  status: "live" | "complete";
  game_type: "cash" | "tournament";
  stakes: string | null;
  big_blind: number | null;
  title: string | null;
  notes: string | null;
  location: string | null;
  currency: string;
  buy_in: number;
  cash_out: number;
  net: number;
  started_at: string;
  duration_minutes: number;
  intention: string | null;
  intention_skill: string | null;
  intention_held: boolean | null;
  mood_before: LiveSession["moodBefore"];
  mood_after: LiveSession["moodAfter"];
  tags: string[];
  notable_hands: RawNotableHand[];
  debrief_conversation_id: string | null;
  created_at: string;
  updated_at: string;
}

interface RawSessionStats {
  session_count: number;
  total_hours: number;
  net_total: number;
  net_per_hour: number;
  bb_per_hour: number | null;
  win_rate_pct: number;
  biggest_win: number;
  biggest_loss: number;
  current_streak: number;
  longest_win_streak: number;
  longest_loss_streak: number;
  std_dev_per_session: number;
  max_drawdown: number;
  form_net: number;
  primary_stakes: string | null;
  confidence: SessionStats["confidence"];
  sample_caveat: string;
}

interface RawBankrollPoint {
  at: string;
  session_id: string;
  net: number;
  cumulative: number;
}

interface RawTrackerHome {
  sessions: RawSession[];
  stats: RawSessionStats;
  bankroll: RawBankrollPoint[];
  open_live_session: RawSession | null;
}

export function mapSession(raw: RawSession): LiveSession {
  return {
    id: raw.id,
    status: raw.status,
    gameType: raw.game_type,
    stakes: raw.stakes,
    bigBlind: raw.big_blind,
    title: raw.title,
    notes: raw.notes,
    location: raw.location,
    currency: raw.currency,
    buyIn: raw.buy_in,
    cashOut: raw.cash_out,
    net: raw.net,
    startedAt: raw.started_at,
    durationMinutes: raw.duration_minutes,
    intention: raw.intention,
    intentionSkill: raw.intention_skill,
    intentionHeld: raw.intention_held,
    moodBefore: raw.mood_before,
    moodAfter: raw.mood_after,
    tags: raw.tags ?? [],
    notableHands: (raw.notable_hands ?? []).map((h) => ({
      summary: h.summary,
      skill: h.skill ?? null,
      outcome: h.outcome ?? null,
      street: h.street ?? null,
    })),
    debriefConversationId: raw.debrief_conversation_id,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

export function mapSessionStats(raw: RawSessionStats): SessionStats {
  return {
    sessionCount: raw.session_count,
    totalHours: raw.total_hours,
    netTotal: raw.net_total,
    netPerHour: raw.net_per_hour,
    bbPerHour: raw.bb_per_hour,
    winRatePct: raw.win_rate_pct,
    biggestWin: raw.biggest_win,
    biggestLoss: raw.biggest_loss,
    currentStreak: raw.current_streak,
    longestWinStreak: raw.longest_win_streak,
    longestLossStreak: raw.longest_loss_streak,
    stdDevPerSession: raw.std_dev_per_session,
    maxDrawdown: raw.max_drawdown,
    formNet: raw.form_net,
    primaryStakes: raw.primary_stakes,
    confidence: raw.confidence,
    sampleCaveat: raw.sample_caveat,
  };
}

export function mapBankrollPoint(raw: RawBankrollPoint): BankrollPoint {
  return {
    at: raw.at,
    sessionId: raw.session_id,
    net: raw.net,
    cumulative: raw.cumulative,
  };
}

export function mapTrackerHome(raw: RawTrackerHome): TrackerHome {
  return {
    sessions: raw.sessions.map(mapSession),
    stats: mapSessionStats(raw.stats),
    bankroll: raw.bankroll.map(mapBankrollPoint),
    openLiveSession: raw.open_live_session
      ? mapSession(raw.open_live_session)
      : null,
  };
}

/** snake_case the body for a log/update request (only defined fields). */
function sessionRequestBody(
  input: Partial<LogSessionInput> & { status?: "complete"; intentionHeld?: boolean | null },
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const set = (k: string, v: unknown) => {
    if (v !== undefined) body[k] = v;
  };
  set("status", input.status);
  set("game_type", input.gameType);
  set("stakes", input.stakes);
  set("buy_in", input.buyIn);
  set("cash_out", input.cashOut);
  set("duration_minutes", input.durationMinutes);
  set("started_at", input.startedAt);
  set("title", input.title);
  set("notes", input.notes);
  set("location", input.location);
  set("currency", input.currency);
  set("intention", input.intention);
  set("intention_skill", input.intentionSkill);
  set("intention_held", input.intentionHeld);
  set("mood_before", input.moodBefore);
  set("mood_after", input.moodAfter);
  set("tags", input.tags);
  set("notable_hands", input.notableHands);
  return body;
}

export async function getTrackerHome(
  filter?: SessionFilter,
): Promise<TrackerHome> {
  const params = new URLSearchParams();
  if (filter?.stake) params.set("stake", filter.stake);
  if (filter?.location) params.set("location", filter.location);
  const qs = params.toString();
  const raw = await getJson<RawTrackerHome>(`/sessions${qs ? `?${qs}` : ""}`);
  return mapTrackerHome(raw);
}

interface RawBreakdown {
  by: BreakdownDimension;
  segments: { key: string; stats: RawSessionStats }[];
}

export async function getSessionBreakdown(
  by: BreakdownDimension,
): Promise<Breakdown> {
  const raw = await getJson<RawBreakdown>(`/sessions/breakdown?by=${by}`);
  return {
    by: raw.by,
    segments: raw.segments.map((s) => ({
      key: s.key,
      stats: mapSessionStats(s.stats),
    })),
  };
}

export async function logSession(input: LogSessionInput): Promise<LiveSession> {
  const res = await authedFetch("/sessions", {
    method: "POST",
    body: sessionRequestBody(input),
  });
  return mapSession((await res.json()) as RawSession);
}

export async function updateSession(
  id: string,
  input: UpdateSessionInput,
): Promise<LiveSession> {
  const res = await authedFetch(`/sessions/${id}`, {
    method: "PATCH",
    body: sessionRequestBody(input),
  });
  return mapSession((await res.json()) as RawSession);
}

export async function deleteSession(id: string): Promise<void> {
  await authedFetch(`/sessions/${id}`, { method: "DELETE" });
}
